import React, { useState, useEffect } from "react";
import Sidebar from "../components/Sidebar";
import Header from "../components/Header";
import { Search, Plus, Trash2, X, Bell, Users, Clock, Send, Calendar, Sparkles } from "lucide-react";
import Swal from 'sweetalert2';
import { API_BASE_URL } from "../config";

export default function Notifications() {
  const [searchTerm, setSearchTerm] = useState("");
  const [audienceFilter, setAudienceFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const [notifications, setNotifications] = useState([]);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [newNotification, setNewNotification] = useState({
    title: "",
    description: "",
    target_audience: "all", // 'all' | 'free' | 'premium'
    send_type: "now",       // 'now' | 'schedule'
    scheduled_at: "",
    time: ""
  });
  const [loading, setLoading] = useState(true);

  // Get current local datetime formatted for min attribute in input (YYYY-MM-DDTHH:mm)
  const getMinDateTime = () => {
    const now = new Date();
    now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
    return now.toISOString().slice(0, 16);
  };

  const fetchNotifications = () => {
    setLoading(true);
    fetch(`${API_BASE_URL}/api/notifications`)
      .then((res) => res.json())
      .then((resJson) => {
        if (resJson && resJson.success) {
          setNotifications(resJson.notifications || []);
        }
        setLoading(false);
      })
      .catch((err) => {
        console.error("Notifications fetch error:", err);
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchNotifications();
  }, []);

  const handleSaveNotification = () => {
    if (!newNotification.title.trim()) {
      Swal.fire("Error", "Please provide a notification title!", "error");
      return;
    }
    if (!newNotification.description.trim()) {
      Swal.fire("Error", "Please provide a description or message!", "error");
      return;
    }

    if (newNotification.send_type === "schedule") {
      if (!newNotification.scheduled_at) {
        Swal.fire("Error", "Please choose a future date and time to schedule the notification!", "error");
        return;
      }
      const selectedTime = new Date(newNotification.scheduled_at).getTime();
      if (selectedTime <= Date.now()) {
        Swal.fire("Error", "Scheduled time must be in the future!", "error");
        return;
      }
    }

    Swal.fire({
      title: newNotification.send_type === "schedule" ? "Scheduling Notification..." : "Sending Push Notification...",
      text: "Please wait.",
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading()
    });

    const payload = {
      title: newNotification.title.trim(),
      description: newNotification.description.trim(),
      target_audience: newNotification.target_audience,
      is_scheduled: newNotification.send_type === "schedule",
      scheduled_at: newNotification.send_type === "schedule" ? newNotification.scheduled_at : null,
      time: newNotification.time.trim() || (newNotification.send_type === "schedule"
        ? new Date(newNotification.scheduled_at).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", dateStyle: "medium", timeStyle: "short" })
        : new Date().toLocaleString("en-IN", { timeZone: "Asia/Kolkata", dateStyle: "medium", timeStyle: "short" }))
    };

    fetch(`${API_BASE_URL}/api/notifications`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload)
    })
      .then(res => res.json())
      .then(resJson => {
        Swal.close();
        if (resJson && resJson.success) {
          Swal.fire({
            icon: "success",
            title: newNotification.send_type === "schedule" ? "Scheduled!" : "Sent Successfully!",
            text: resJson.message || "Notification processed successfully."
          });
          fetchNotifications();
          setNewNotification({
            title: "",
            description: "",
            target_audience: "all",
            send_type: "now",
            scheduled_at: "",
            time: ""
          });
          setIsCreateModalOpen(false);
        } else {
          throw new Error(resJson.message || "Failed to save notification");
        }
      })
      .catch(err => {
        Swal.close();
        console.error("Save notification error:", err);
        Swal.fire("Error", err.message, "error");
      });
  };

  const handleDeleteNotification = (id) => {
    Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this notification!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'Yes, delete it!'
    }).then((result) => {
      if (result.isConfirmed) {
        Swal.fire({ title: "Deleting...", allowOutsideClick: false, didOpen: () => Swal.showLoading() });
        fetch(`${API_BASE_URL}/api/notifications/${id}`, { method: "DELETE" })
          .then(res => res.json())
          .then(resJson => {
            Swal.close();
            if (resJson && resJson.success) {
              Swal.fire('Deleted!', 'Notification has been removed.', 'success');
              fetchNotifications();
            } else {
              throw new Error(resJson.message || "Failed to delete notification");
            }
          })
          .catch(err => {
            Swal.close();
            console.error("Delete error:", err);
            Swal.fire("Error", err.message, "error");
          });
      }
    });
  };

  const getAudienceBadge = (audience) => {
    switch (audience) {
      case "free":
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-indigo-50 dark:bg-indigo-950/40 text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800">
            <span className="w-1.5 h-1.5 rounded-full bg-indigo-500"></span>
            Free Users
          </span>
        );
      case "premium":
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-800">
            <Sparkles size={12} className="text-amber-500" />
            Premium Users
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800">
            <Users size={12} className="text-blue-500" />
            All Users
          </span>
        );
    }
  };

  const getStatusBadge = (status, scheduledAt) => {
    if (status === "pending") {
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-orange-50 dark:bg-orange-950/40 text-orange-700 dark:text-orange-300 border border-orange-200 dark:border-orange-800" title={scheduledAt ? `Scheduled for: ${scheduledAt}` : "Pending"}>
          <Clock size={12} className="text-orange-500 animate-pulse" />
          Scheduled
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800">
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
        Sent
      </span>
    );
  };

  const filteredNotifications = notifications.filter(n => {
    const matchesSearch = n.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      n.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesAudience = audienceFilter === "all" || (n.target_audience || "all") === audienceFilter;
    const matchesStatus = statusFilter === "all" || (n.status || "sent") === statusFilter;
    return matchesSearch && matchesAudience && matchesStatus;
  });

  return (
    <div className="flex bg-[#f8fafc] dark:bg-slate-900 min-h-screen text-slate-800 dark:text-slate-100 font-sans selection:bg-blue-100 dark:selection:bg-blue-900 relative overflow-hidden">
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />

      <div className="flex-1 lg:ml-64 flex flex-col min-h-screen relative z-10 w-full lg:w-[calc(100%-16rem)]">
        <Header
          onMenuClick={() => setIsSidebarOpen(true)}
          title="Notifications Management"
          subtitle="Send push notifications to Free, Premium, or All users now or schedule for later."
        />

        <div className="p-4 sm:p-8 flex-1 flex flex-col w-full">
          {/* Header Action Section */}
          <div className="mb-6 sm:mb-8 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h2 className="text-xl font-bold text-slate-800 dark:text-slate-100 flex items-center gap-2">
                <Bell className="text-blue-600" size={24} />
                Broadcast Notifications
              </h2>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                Target Free or Premium users and schedule delivery time.
              </p>
            </div>

            <button
              onClick={() => {
                setNewNotification({
                  title: "",
                  description: "",
                  target_audience: "all",
                  send_type: "now",
                  scheduled_at: "",
                  time: ""
                });
                setIsCreateModalOpen(true);
              }}
              className="justify-center bg-blue-600 hover:bg-blue-700 text-white shadow-sm hover:shadow-md px-5 py-2.5 rounded-xl text-sm font-semibold transition-all flex items-center gap-2 cursor-pointer"
            >
              <Plus size={18} />
              Send / Schedule Notification
            </button>
          </div>

          {/* Table Card */}
          <div className="bg-white dark:bg-slate-800 shadow-sm rounded-2xl border border-slate-200 dark:border-slate-700 overflow-hidden flex flex-col flex-1">
            {/* Toolbar Filters */}
            <div className="p-4 sm:p-5 border-b border-slate-100 dark:border-slate-700/60 flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="relative w-full md:w-80">
                <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  placeholder="Search by title or description..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl pl-10 pr-4 py-2 text-sm outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 transition-all shadow-sm placeholder-slate-400"
                />
              </div>

              <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
                <select
                  value={audienceFilter}
                  onChange={(e) => setAudienceFilter(e.target.value)}
                  className="bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl px-3.5 py-2 text-xs font-medium text-slate-700 dark:text-slate-300 outline-none focus:border-blue-500 cursor-pointer"
                >
                  <option value="all">All Audiences</option>
                  <option value="free">Free Users Only</option>
                  <option value="premium">Premium Users Only</option>
                </select>

                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl px-3.5 py-2 text-xs font-medium text-slate-700 dark:text-slate-300 outline-none focus:border-blue-500 cursor-pointer"
                >
                  <option value="all">All Status</option>
                  <option value="sent">Sent</option>
                  <option value="pending">Scheduled</option>
                </select>
              </div>
            </div>

            {/* Table Container */}
            <div className="overflow-x-auto flex-1">
              <table className="w-full text-left border-collapse whitespace-nowrap min-w-max">
                <thead className="bg-slate-50/70 dark:bg-slate-900/50 border-b border-slate-100 dark:border-slate-700/50">
                  <tr>
                    <th className="p-4 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider sticky left-0 bg-slate-50 dark:bg-slate-900/50 z-20">Notification</th>
                    <th className="p-4 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Target Audience</th>
                    <th className="p-4 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Status</th>
                    <th className="p-4 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Delivery Time</th>
                    <th className="p-4 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-700/50">
                  {loading ? (
                    <tr>
                      <td colSpan="5" className="p-10 text-center text-slate-500 dark:text-slate-400 animate-pulse">
                        Loading notifications...
                      </td>
                    </tr>
                  ) : filteredNotifications.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="p-12 text-center text-slate-500 dark:text-slate-400">
                        <div className="flex flex-col items-center justify-center gap-2">
                          <Bell className="w-8 h-8 text-slate-300 dark:text-slate-600" />
                          <p className="font-medium">No notifications found.</p>
                          <span className="text-xs text-slate-400">Click "Send / Schedule Notification" to broadcast.</span>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    filteredNotifications.map((notif) => (
                      <tr key={notif.notification_id_PK} className="hover:bg-slate-50/80 dark:hover:bg-slate-700/60 transition-colors group">
                        <td className="p-4 sticky left-0 bg-white dark:bg-slate-800 group-hover:bg-slate-50 dark:group-hover:bg-slate-700/50 z-10 transition-colors max-w-sm">
                          <div className="flex items-start gap-3">
                            <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 mt-0.5">
                              <Bell size={20} />
                            </div>
                            <div className="min-w-0">
                              <h4 className="text-sm font-semibold text-slate-800 dark:text-slate-100 truncate" title={notif.title}>
                                {notif.title}
                              </h4>
                              <p className="text-xs text-slate-500 dark:text-slate-400 truncate max-w-xs mt-0.5" title={notif.description}>
                                {notif.description}
                              </p>
                            </div>
                          </div>
                        </td>

                        <td className="p-4">
                          {getAudienceBadge(notif.target_audience || "all")}
                        </td>

                        <td className="p-4">
                          {getStatusBadge(notif.status, notif.scheduled_at)}
                        </td>

                        <td className="p-4 text-xs text-slate-600 dark:text-slate-400">
                          {notif.status === 'pending' && notif.scheduled_at ? (
                            <span className="flex items-center gap-1.5 text-orange-600 dark:text-orange-400 font-medium">
                              <Clock size={13} />
                              Scheduled: {new Date(notif.scheduled_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', dateStyle: 'medium', timeStyle: 'short' })}
                            </span>
                          ) : notif.time ? (
                            notif.time
                          ) : (
                            new Date(notif.created_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', dateStyle: 'medium', timeStyle: 'short' })
                          )}
                        </td>

                        <td className="p-4 text-right">
                          <button
                            onClick={() => handleDeleteNotification(notif.notification_id_PK)}
                            className="p-2 text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-500/10 rounded-lg transition-colors cursor-pointer"
                            title="Delete Notification"
                          >
                            <Trash2 size={17} />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Create / Schedule Modal */}
      {isCreateModalOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={() => setIsCreateModalOpen(false)}></div>
          <div className="bg-white dark:bg-slate-800 rounded-2xl p-6 sm:p-7 w-full max-w-lg relative z-10 shadow-2xl border border-slate-200 dark:border-slate-700 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-5 pb-3 border-b border-slate-100 dark:border-slate-700">
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-xl bg-blue-50 dark:bg-blue-900/40 text-blue-600 flex items-center justify-center">
                  <Send size={18} />
                </div>
                <div>
                  <h3 className="text-base font-bold text-slate-800 dark:text-white">Create Broadcast Notification</h3>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Target audience & delivery time</p>
                </div>
              </div>
              <button onClick={() => setIsCreateModalOpen(false)} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 cursor-pointer p-1">
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4">
              {/* Target Audience Selector */}
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 dark:text-slate-300 mb-2">
                  1. Target Audience
                </label>
                <div className="grid grid-cols-3 gap-2.5">
                  <button
                    type="button"
                    onClick={() => setNewNotification({ ...newNotification, target_audience: "all" })}
                    className={`p-3 rounded-xl border text-left flex flex-col gap-1 transition-all cursor-pointer ${newNotification.target_audience === "all"
                        ? "border-blue-500 bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300 ring-2 ring-blue-500/20"
                        : "border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600 text-slate-700 dark:text-slate-300"
                      }`}
                  >
                    <div className="flex items-center justify-between">
                      <Users size={16} />
                      {newNotification.target_audience === "all" && <span className="w-2 h-2 rounded-full bg-blue-600"></span>}
                    </div>
                    <span className="text-xs font-bold">All Users</span>
                    <span className="text-[10px] text-slate-500 dark:text-slate-400">Sabhi users ko</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => setNewNotification({ ...newNotification, target_audience: "free" })}
                    className={`p-3 rounded-xl border text-left flex flex-col gap-1 transition-all cursor-pointer ${newNotification.target_audience === "free"
                        ? "border-indigo-500 bg-indigo-50 dark:bg-indigo-950/40 text-indigo-700 dark:text-indigo-300 ring-2 ring-indigo-500/20"
                        : "border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600 text-slate-700 dark:text-slate-300"
                      }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-sm">🆓</span>
                      {newNotification.target_audience === "free" && <span className="w-2 h-2 rounded-full bg-indigo-600"></span>}
                    </div>
                    <span className="text-xs font-bold">Free Users</span>
                    <span className="text-[10px] text-slate-500 dark:text-slate-400">Non-paid users</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => setNewNotification({ ...newNotification, target_audience: "premium" })}
                    className={`p-3 rounded-xl border text-left flex flex-col gap-1 transition-all cursor-pointer ${newNotification.target_audience === "premium"
                        ? "border-amber-500 bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300 ring-2 ring-amber-500/20"
                        : "border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600 text-slate-700 dark:text-slate-300"
                      }`}
                  >
                    <div className="flex items-center justify-between">
                      <Sparkles size={16} className="text-amber-500" />
                      {newNotification.target_audience === "premium" && <span className="w-2 h-2 rounded-full bg-amber-600"></span>}
                    </div>
                    <span className="text-xs font-bold">Premium Users</span>
                    <span className="text-[10px] text-slate-500 dark:text-slate-400">Active paid users</span>
                  </button>
                </div>
              </div>

              {/* Title */}
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 dark:text-slate-300 mb-1.5">
                  2. Title  *
                </label>
                <input
                  type="text"
                  value={newNotification.title}
                  onChange={e => setNewNotification({ ...newNotification, title: e.target.value })}
                  placeholder="e.g. 🎉 Special Festival Offer / Daily Darshan Ready"
                  className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:text-white"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 dark:text-slate-300 mb-1.5">
                  3. Description / Message  *
                </label>
                <textarea
                  rows="3"
                  value={newNotification.description}
                  onChange={e => setNewNotification({ ...newNotification, description: e.target.value })}
                  placeholder="Write your push notification message here..."
                  className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:text-white resize-none"
                />
              </div>

              {/* Delivery Timing Options */}
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 dark:text-slate-300 mb-2">
                  4. Delivery Timing
                </label>
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <button
                    type="button"
                    onClick={() => setNewNotification({ ...newNotification, send_type: "now" })}
                    className={`py-2.5 px-3 rounded-xl border text-center flex items-center justify-center gap-2 transition-all cursor-pointer text-xs font-semibold ${newNotification.send_type === "now"
                        ? "border-blue-500 bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300 ring-2 ring-blue-500/20"
                        : "border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600 text-slate-700 dark:text-slate-300"
                      }`}
                  >
                    <Send size={14} />
                    Send Immediately
                  </button>

                  <button
                    type="button"
                    onClick={() => setNewNotification({ ...newNotification, send_type: "schedule" })}
                    className={`py-2.5 px-3 rounded-xl border text-center flex items-center justify-center gap-2 transition-all cursor-pointer text-xs font-semibold ${newNotification.send_type === "schedule"
                        ? "border-orange-500 bg-orange-50 dark:bg-orange-950/40 text-orange-700 dark:text-orange-300 ring-2 ring-orange-500/20"
                        : "border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600 text-slate-700 dark:text-slate-300"
                      }`}
                  >
                    <Clock size={14} />
                    Schedule for Later
                  </button>
                </div>

                {newNotification.send_type === "schedule" && (
                  <div className="bg-orange-50/50 dark:bg-orange-950/20 border border-orange-200 dark:border-orange-900/50 rounded-xl p-3.5 space-y-2">
                    <label className="block text-xs font-medium text-orange-800 dark:text-orange-300 flex items-center gap-1.5">
                      <Calendar size={14} />
                      Select Date & Time (IST):
                    </label>
                    <input
                      type="datetime-local"
                      min={getMinDateTime()}
                      value={newNotification.scheduled_at}
                      onChange={e => setNewNotification({ ...newNotification, scheduled_at: e.target.value })}
                      className="w-full bg-white dark:bg-slate-900 border border-orange-300 dark:border-orange-800 rounded-xl px-3.5 py-2 text-sm outline-none focus:ring-2 focus:ring-orange-400 dark:text-white"
                    />
                    {newNotification.scheduled_at && (
                      <p className="text-[11px] text-orange-700 dark:text-orange-400">
                        Will be automatically dispatched on: <strong>{new Date(newNotification.scheduled_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', dateStyle: 'full', timeStyle: 'short' })}</strong>
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="mt-7 pt-4 border-t border-slate-100 dark:border-slate-700 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setIsCreateModalOpen(false)}
                className="px-4 py-2 bg-slate-100 dark:bg-slate-700 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-600 rounded-xl text-sm font-medium transition-colors cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveNotification}
                className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-semibold transition-all shadow-sm hover:shadow-md flex items-center gap-2 cursor-pointer"
              >
                {newNotification.send_type === "schedule" ? (
                  <>
                    <Clock size={16} />
                    Schedule Notification
                  </>
                ) : (
                  <>
                    <Send size={16} />
                    Send Notification Now
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
